import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { AdminAnnouncementsPanel } from '../components/AdminAnnouncementsPanel.jsx';
import { AttentionItem } from '../components/AttentionItem.jsx';
import { MyWorkPanel } from '../components/MyWorkPanel.jsx';
import { DashboardIntegrationsPanel } from '../components/DashboardIntegrationsPanel.jsx';
import { DashboardStatCard } from '../components/DashboardStatCard.jsx';
import { PageWelcome } from '../components/PageWelcome.jsx';
import { ProgressBar } from '../components/ProgressBar.jsx';
import { StatusBadge } from '../components/StatusBadge.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { formatDate, formatDateTime } from '../utils/formatDate.js';
import { projectHealth } from '../utils/statusUi.js';
import { firstName, initials, timeGreeting } from '../utils/userDisplay.js';
import { clientLabel } from '../utils/clientLabel.js';

const PROJECT_FILTERS = [
  { id: 'all', label: 'All Projects' },
  { id: 'working', label: 'ViiB Working' },
  { id: 'waiting', label: 'Waiting on Client' },
  { id: 'attention', label: 'Needs Attention' },
  { id: 'soon', label: 'Due Soon' },
];

export function AdminOverviewPage() {
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [scope, setScope] = useState('agency');
  const [projectFilter, setProjectFilter] = useState('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    apiJson('/api/admin/dashboard')
      .then(setStats)
      .catch((e) => setError(e.message));
  }, []);

  const attention = useMemo(() => {
    if (!stats) return [];
    const items = [];
    if ((stats.projects_overdue ?? 0) > 0) {
      items.push({
        tone: 'danger',
        title: `${stats.projects_overdue} overdue project${stats.projects_overdue === 1 ? '' : 's'}`,
        detail: 'These items are past their due date',
        to: '/admin/projects',
        action: 'Review',
      });
    }
    if (stats.unread_notifications > 0) {
      items.push({
        tone: 'danger',
        title: 'Unread alerts',
        detail: `${stats.unread_notifications} need review`,
        to: '/admin/notifications?unread=1',
        action: 'Review',
      });
    }
    if ((stats.unread_form_submissions ?? 0) > 0 && caps.faq_forms) {
      items.push({
        tone: 'warning',
        title: 'Pending forms',
        detail: `${stats.unread_form_submissions} submissions waiting`,
        to: '/admin/forms?filter=unread',
        action: 'Review',
      });
    }
    if ((stats.pending_asset_requests ?? 0) > 0 && caps.manage_assets) {
      items.push({
        tone: 'warning',
        title: 'Asset requests',
        detail: `${stats.pending_asset_requests} waiting on clients`,
        to: '/admin/assets?tab=requests',
        action: 'View',
      });
    }
    if ((stats.pending_asset_approvals ?? 0) > 0 && caps.manage_assets) {
      items.push({
        tone: 'info',
        title: 'Pending approvals',
        detail: `${stats.pending_asset_approvals} files need approval`,
        to: '/admin/assets?tab=approvals',
        action: 'Review',
      });
    }
    if (stats.clients_near_time_limit > 0 && caps.time_tracker) {
      items.push({
        tone: 'danger',
        title: 'Near time limit',
        detail: `${stats.clients_near_time_limit} clients are running low`,
        to: '/admin/time-tracker?near_limit=1',
        action: 'View',
      });
    }
    if (stats.clients_without_ghl > 0 && caps.view_contacts) {
      items.push({
        tone: 'info',
        title: 'Missing GHL contact',
        detail: `${stats.clients_without_ghl} clients are not linked`,
        to: '/admin/contacts?ghl=missing',
        action: 'View',
      });
    }
    (stats.recent_sync_failures || []).slice(0, 2).forEach((f) => {
      items.push({
        tone: 'danger',
        title: `${f.provider} sync failed`,
        detail: f.message || 'Integration error',
        to: '/admin/integrations',
        action: 'Review',
      });
    });
    return items;
  }, [stats, caps]);

  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      }),
    [],
  );

  const visibleProjects = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const soon = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
    return (stats?.active_projects || []).filter((p) => {
      if (scope === 'mine' && !p.assigned_to_me) return false;
      const q = query.trim().toLowerCase();
      if (q) {
        const hay = `${p.name} ${p.client?.company_name || p.client?.name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const health = projectHealth(p);
      if (projectFilter === 'working') return health.tone === 'success';
      if (projectFilter === 'waiting') return health.tone === 'warning' || p.waiting_on_client;
      if (projectFilter === 'attention') return health.tone === 'danger' || p.is_overdue;
      if (projectFilter === 'soon') return Boolean(p.due_date) && p.due_date >= today && p.due_date <= soon;
      return true;
    });
  }, [stats, scope, projectFilter, query]);

  if (error) {
    return <p className="err">{error}</p>;
  }

  if (!stats) {
    return <p className="muted">Loading dashboard…</p>;
  }

  const dueToday = (stats.my_tasks || []).filter((t) => t.due_date && t.due_date.slice(0, 10) === new Date().toISOString().slice(0, 10));

  return (
    <div>
      <PageWelcome
        title={`${timeGreeting()}, ${firstName(user?.name)}`}
        subtitle="Here’s what’s happening across your agency today."
        actions={
          <>
            {caps.manage_projects ? (
              <Link to="/admin/projects?create=1" className="btn-primary">
                + New Project
              </Link>
            ) : null}
            {caps.manage_clients ? (
              <Link to="/admin/clients?create=1" className="btn-secondary">
                + New Client
              </Link>
            ) : null}
            {caps.view_mailbox ? (
              <Link to="/admin/meetings" className="btn-secondary">
                View Calendar
              </Link>
            ) : null}
            <div className="scope-toggle" role="group" aria-label="Dashboard scope">
              <button type="button" className={scope === 'agency' ? 'is-active' : ''} onClick={() => setScope('agency')}>
                All Agency
              </button>
              <button type="button" className={scope === 'mine' ? 'is-active' : ''} onClick={() => setScope('mine')}>
                My Work
              </button>
            </div>
          </>
        }
      />

      <MyWorkPanel work={stats.my_work} locale={user?.ui_locale} />

      {stats.priority_support ? (
        <p className="panel" style={{ marginBottom: '1rem', borderLeft: '4px solid var(--primary)' }}>
          Priority support is active on your plan.
        </p>
      ) : null}

      <div className="kpi-grid" data-tour="tour-dashboard-stats">
        <DashboardStatCard
          label="Active Projects"
          value={stats.projects_active}
          tone="purple"
          to={caps.manage_projects ? '/admin/projects?status=active' : undefined}
        />
        <DashboardStatCard
          label="Needs Attention"
          value={attention.length}
          tone={attention.length ? 'danger' : 'success'}
        />
        <DashboardStatCard
          label="Waiting on Client"
          value={stats.projects_waiting_on_client ?? 0}
          tone="warning"
        />
        <DashboardStatCard
          label="Due Soon"
          value={stats.projects_due_soon ?? 0}
          tone="info"
          hint="Next 7 days"
          to={caps.manage_projects ? '/admin/projects' : undefined}
        />
        <DashboardStatCard
          label="Overdue"
          value={stats.projects_overdue ?? 0}
          tone={(stats.projects_overdue ?? 0) ? 'danger' : 'default'}
        />
      </div>

      <div className="kpi-mini-row">
        {caps.manage_clients ? (
          <Link to="/admin/clients" className="kpi-mini">
            <span className="muted small">Clients</span>
            <strong>{stats.clients_total}</strong>
          </Link>
        ) : (
          <div className="kpi-mini">
            <span className="muted small">Clients</span>
            <strong>{stats.clients_total}</strong>
          </div>
        )}
        {caps.faq_forms ? (
          <Link to="/admin/forms?filter=unread" className="kpi-mini">
            <span className="muted small">Pending Forms</span>
            <strong>{stats.unread_form_submissions ?? 0}</strong>
          </Link>
        ) : (
          <div className="kpi-mini">
            <span className="muted small">Pending Forms</span>
            <strong>{stats.unread_form_submissions ?? 0}</strong>
          </div>
        )}
        {caps.manage_assets ? (
          <Link to="/admin/assets?tab=requests" className="kpi-mini">
            <span className="muted small">Asset Requests</span>
            <strong>{stats.pending_asset_requests ?? 0}</strong>
          </Link>
        ) : (
          <div className="kpi-mini">
            <span className="muted small">Asset Requests</span>
            <strong>{stats.pending_asset_requests ?? 0}</strong>
          </div>
        )}
        {caps.manage_assets ? (
          <Link to="/admin/assets?tab=approvals" className="kpi-mini">
            <span className="muted small">Approvals</span>
            <strong>{stats.pending_asset_approvals ?? 0}</strong>
          </Link>
        ) : (
          <div className="kpi-mini">
            <span className="muted small">Approvals</span>
            <strong>{stats.pending_asset_approvals ?? 0}</strong>
          </div>
        )}
      </div>

      <div className="dash-grid">
        <section className="panel">
          <div className="panel-head">
            <h3 className="panel-title">Active Projects</h3>
            {caps.manage_projects ? (
              <Link to="/admin/projects" className="panel-link">
                View all →
              </Link>
            ) : null}
          </div>
          <div className="chip-tabs">
            {PROJECT_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`chip-tab${projectFilter === f.id ? ' is-active' : ''}`}
                onClick={() => setProjectFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <label className="field panel-search" style={{ marginBottom: '0.85rem' }}>
            <span className="sr-only">Search projects</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search company or project…"
            />
          </label>
          {visibleProjects.length ? (
            <div className="table-wrap" style={{ boxShadow: 'none', border: 0 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client / Project</th>
                    <th>Status</th>
                    <th>Progress</th>
                    <th>Next Action</th>
                    <th>Due Date</th>
                    <th>Lead</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleProjects.map((p) => {
                    const health = projectHealth(p);
                    return (
                      <tr key={p.id}>
                        <td>
                          <Link to={`/admin/projects/${p.id}`}>
                            <strong>{clientLabel(p.client) || 'Client'}</strong>
                          </Link>
                          <div className="muted small">{p.name}</div>
                        </td>
                        <td>
                          <StatusBadge tone={health.tone} label={health.label} />
                        </td>
                        <td style={{ minWidth: '8rem' }}>
                          <ProgressBar value={p.progress_percent ?? 0} tone={health.tone === 'danger' ? 'danger' : health.tone === 'warning' ? 'warning' : 'success'} />
                        </td>
                        <td>{p.next_action || '—'}</td>
                        <td>{p.due_date ? formatDate(p.due_date) : '—'}</td>
                        <td>
                          {p.lead ? (
                            <span className="lead-chip">
                              <span className="lead-avatar">{initials(p.lead)}</span>
                              {p.lead}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-state">No projects match this view.</p>
          )}
        </section>

        <section className="panel">
          <div className="panel-head">
            <h3 className="panel-title">Needs Attention</h3>
          </div>
          {attention.length ? (
            <div className="attention-list">
              {attention.map((item, i) => (
                <AttentionItem key={`${item.title}-${i}`} {...item} />
              ))}
            </div>
          ) : (
            <p className="empty-state">Nothing urgent right now.</p>
          )}
        </section>
      </div>

      <div className="dash-grid-3">
        <section className="panel">
          <div className="panel-head">
            <h3 className="panel-title">Today</h3>
            <Link to="/admin/meetings" className="panel-link">
              Calendar →
            </Link>
          </div>
          <p className="muted small" style={{ marginTop: 0 }}>
            {todayLabel}
          </p>
          {stats.upcoming_meetings?.length ? (
            <ul className="feed-list">
              {stats.upcoming_meetings.map((m) => (
                <li key={m.id}>
                  <span>
                    <strong>{m.subject}</strong>
                  </span>
                  <span className="muted small">{m.start_at ? formatDateTime(m.start_at) : ''}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="empty-state">No meetings on the calendar.</p>
          )}
          {dueToday.length ? (
            <>
              <h3 className="panel-title" style={{ marginTop: '1rem' }}>
                Due today
              </h3>
              <ul className="feed-list">
                {dueToday.map((t) => (
                  <li key={t.id}>
                    <span>
                      {t.title}
                      {t.project?.name ? ` · ${t.project.name}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </section>

        <section className="panel">
          <div className="panel-head">
            <h3 className="panel-title">Recent Activity</h3>
          </div>
          {stats.recent_activity?.length ? (
            <ul className="feed-list">
              {stats.recent_activity.map((a) => (
                <li key={a.id}>
                  <span>
                    <strong>{a.title}</strong>
                    {a.project?.name ? <div className="muted small">{a.project.name}</div> : null}
                  </span>
                  <span className="muted small">{formatDateTime(a.created_at)}</span>
                </li>
              ))}
            </ul>
          ) : stats.recent_emails?.length ? (
            <ul className="feed-list">
              {stats.recent_emails.map((e) => (
                <li key={e.id}>
                  <span>
                    <strong>{e.subject}</strong>
                    {e.from_email ? <div className="muted small">{e.from_email}</div> : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="empty-state">No recent activity yet.</p>
          )}
        </section>

        <section className="panel">
          <div className="panel-head">
            <h3 className="panel-title">Notifications</h3>
            <Link to="/admin/notifications" className="panel-link">
              All →
            </Link>
          </div>
          {stats.recent_notifications?.length ? (
            <ul className="feed-list">
              {stats.recent_notifications.map((n) => (
                <li key={n.id}>
                  <span className={n.read_at ? 'muted' : ''}>
                    <strong>{n.title}</strong>
                    {n.body ? <div className="muted small">{n.body}</div> : null}
                  </span>
                  <span className="muted small">{formatDateTime(n.created_at)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="empty-state">No notifications yet.</p>
          )}
        </section>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <DashboardIntegrationsPanel integrations={stats.integrations} />
      </div>

      {stats.announcements?.length ? (
        <section className="panel" style={{ marginTop: '1rem' }}>
          <h3 className="panel-title">Company News</h3>
          <ul className="feed-list">
            {stats.announcements.map((a) => (
              <li key={a.id}>
                <span>
                  <strong>{a.title}</strong>
                  {a.body ? <div className="muted small">{a.body}</div> : null}
                </span>
                <span className="muted small">{formatDateTime(a.published_at)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <AdminAnnouncementsPanel />
    </div>
  );
}
